
import { Question } from '../types';

export const qur101StandardizationQuestions: Question[] = [
  {
    id: 'qur081',
    text: 'What happened to the manuscript compiled by Zaid ibn Thābit after Abu Bakr\'s death?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'It was given to Umar RA', isCorrect: true },
      { id: 'b', text: 'After Umar\'s death, it was given to Hafsa RA', isCorrect: true },
      { id: 'c', text: 'It became the basis for Uthman\'s standardization', isCorrect: true },
      { id: 'd', text: 'It was distributed to different regions immediately', isCorrect: false }
    ],
    explanation: 'After Abu Bakr\'s death, the manuscript compiled by Zaid ibn Thābit was passed to Umar RA. Following Umar\'s death, it was given to his daughter Hafsa RA for safekeeping. Later, during Uthman\'s caliphate, this manuscript became the primary reference for the standardization of the Qur\'ānic text.',
    topic: 'qur101'
  },
  {
    id: 'qur082',
    text: 'What issues arose with the Qur\'ān during Uthman\'s caliphate?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Muslims were reciting with different aḥruf without knowing others were valid', isCorrect: true },
      { id: 'b', text: 'People were doing takfīr of each other over different recitations', isCorrect: true },
      { id: 'c', text: 'The expansion of Islam created a decentralized system of knowledge', isCorrect: true },
      { id: 'd', text: 'The original text was being deliberately corrupted', isCorrect: false }
    ],
    explanation: 'During Uthman\'s caliphate, several issues arose with the Qur\'ān: Muslims were reciting with different aḥruf (legitimate variants) without knowing others were valid; people were doing takfīr (declaring others unbelievers) over different recitations; and the rapid expansion of Islam created a decentralized system of knowledge where people couldn\'t easily verify what they were hearing against an authoritative source.',
    topic: 'qur101'
  },
  {
    id: 'qur083',
    text: 'What were the characteristics of Uthman ibn Affān RA as a person and leader?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Extremely shy and modest', isCorrect: true },
      { id: 'b', text: 'Generous', isCorrect: true },
      { id: 'c', text: 'Faced Muslims in conflict about what Islam should be like', isCorrect: true },
      { id: 'd', text: 'Known for military conquests', isCorrect: false }
    ],
    explanation: 'Uthman ibn Affān RA was characterized as an extremely shy, modest, and generous person. As a leader, he came to the caliphate at a challenging time when Muslims were in conflict about what they thought Islam should be and should be like. Unlike some other caliphs, he was not primarily known for military conquests but for his character and administrative decisions.',
    topic: 'qur101'
  },
  {
    id: 'qur084',
    text: 'Who alerted Uthman RA to the problems with different Qur\'ānic recitations?',
    type: 'single',
    options: [
      { id: 'a', text: 'Hudhaifah ibn al-Yaman', isCorrect: true },
      { id: 'b', text: 'Zaid ibn Thābit', isCorrect: false },
      { id: 'c', text: 'Ali ibn Abi Talib', isCorrect: false },
      { id: 'd', text: 'Abdullah ibn Masud', isCorrect: false }
    ],
    explanation: 'Hudhaifah ibn al-Yaman alerted Uthman RA to the problems with different Qur\'ānic recitations. After participating in campaigns where Muslims from different regions were reciting differently and accusing each other of error, Hudhaifah warned Uthman that these differences could lead to serious division within the community.',
    topic: 'qur101'
  },
  {
    id: 'qur085',
    text: 'What was the significance of takfīr occurring over different recitations?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'It had legal implications, as apostasy was considered treason', isCorrect: true },
      { id: 'b', text: 'It threatened the unity of the Muslim community', isCorrect: true },
      { id: 'c', text: 'It indicated a lack of knowledge about the valid aḥruf', isCorrect: true },
      { id: 'd', text: 'It was encouraged to purify recitation methods', isCorrect: false }
    ],
    explanation: 'The occurrence of takfīr (declaring others unbelievers) over different recitations was extremely serious because: it had legal implications, as apostasy was considered treason with potential punishments; it threatened the unity of the Muslim community; and it indicated a lack of knowledge that different recitations (aḥruf) were divinely approved. These factors made standardization urgent and necessary.',
    topic: 'qur101'
  },
  {
    id: 'qur086',
    text: 'What process did Uthman RA use to standardize the Qur\'ān?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'He recruited Zaid ibn Thābit for his expertise', isCorrect: true },
      { id: 'b', text: 'He created multiple standardized manuscripts', isCorrect: true },
      { id: 'c', text: 'He sent teachers with the manuscripts to different regions', isCorrect: true },
      { id: 'd', text: 'He added his own interpretations to clarify meaning', isCorrect: false }
    ],
    explanation: 'To standardize the Qur\'ān, Uthman RA: recruited Zaid ibn Thābit (who had compiled the first official manuscript under Abu Bakr) for his expertise; created multiple standardized manuscripts based on the seven aḥruf; and sent teachers along with these manuscripts to different regions to teach proper recitation. The standardization was a careful process of preserving authentic variants while ensuring uniformity.',
    topic: 'qur101'
  },
  {
    id: 'qur087',
    text: 'What did Uthman RA order to be done with personal manuscripts of the Qur\'ān?',
    type: 'single',
    options: [
      { id: 'a', text: 'To be burned, except for confirmed correct ones from companions', isCorrect: true },
      { id: 'b', text: 'To be stored in a central repository', isCorrect: false },
      { id: 'c', text: 'To be modified to match the standard', isCorrect: false },
      { id: 'd', text: 'To be maintained alongside the standard version', isCorrect: false }
    ],
    explanation: 'Uthman RA ordered personal manuscripts of the Qur\'ān to be burned, except for confirmed correct ones from the companions and some others. This wasn\'t due to problems with legitimate manuscripts but because there were some with errors or unclear distinctions between the Qur\'ānic text and personal notes. People could make new manuscripts under the guidance of teachers using the standard copies.',
    topic: 'qur101'
  },
  {
    id: 'qur088',
    text: 'What is the significance of the original manuscripts being returned to Madinah?',
    type: 'single',
    options: [
      { id: 'a', text: 'To maintain a central reference point as Islam continued to expand', isCorrect: true },
      { id: 'b', text: 'To prevent non-Arabs from accessing the original', isCorrect: false },
      { id: 'c', text: 'Because they were wearing out from frequent use', isCorrect: false },
      { id: 'd', text: 'To add new revelations as they occurred', isCorrect: false }
    ],
    explanation: 'The significance of the original manuscripts being returned to Madinah was to maintain a central reference point as Islam continued to expand. This ensured that as Muslims spread to new regions, teachers could return to Madinah to verify their copies against the original standards, then take that knowledge back to their communities, maintaining accuracy and consistency.',
    topic: 'qur101'
  },
  {
    id: 'qur089',
    text: 'What later ruler contributed to the codification process by adding diacritical marks?',
    type: 'single',
    options: [
      { id: 'a', text: 'Ḥajjāj ibn Yūsuf', isCorrect: true },
      { id: 'b', text: 'Abd al-Malik ibn Marwan', isCorrect: false },
      { id: 'c', text: 'Muawiyah ibn Abi Sufyan', isCorrect: false },
      { id: 'd', text: 'Al-Walid ibn Abd al-Malik', isCorrect: false }
    ],
    explanation: 'Ḥajjāj ibn Yūsuf later contributed to the codification process by adding ḥarakāt (diacritical marks) to the maṣāḥif (copies of the Qur\'ān). These marks helped non-Arab Muslims and those less familiar with Arabic to read the text correctly by indicating vowels and pronunciation.',
    topic: 'qur101'
  },
  {
    id: 'qur090',
    text: 'Why was the problem with manuscripts not primarily with the manuscripts themselves?',
    type: 'single',
    options: [
      { id: 'a', text: 'The problem was with takfīr over differences and mistakes from manuscripts not in knowledgeable hands', isCorrect: true },
      { id: 'b', text: 'The manuscripts were being deliberately altered by enemies of Islam', isCorrect: false },
      { id: 'c', text: 'The problem was with the quality of materials used for the manuscripts', isCorrect: false },
      { id: 'd', text: 'There weren\'t enough manuscripts to meet demand', isCorrect: false }
    ],
    explanation: 'The problem was not primarily with the manuscripts themselves but with the takfīr (declaring others unbelievers) occurring due to differences and due to mistakes arising from manuscripts that weren\'t in the hands of those with knowledge. People unfamiliar with the legitimate variants or unable to distinguish between the text and notes were making accusations of error, leading to serious social consequences.',
    topic: 'qur101'
  }
];
